<template>
  <teleport to="#modal">
    <DialogModal
      ref="dialog"
      v-body-scroll-lock="true"
      class="participants-dialog"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('status.who_interacted')"
      :on-cancel="close"
    >
      <template #header>
        {{ $t('status.who_interacted') }}
      </template>

      <!-- one pill per list, like the post's star and reaction pills -->
      <div
        class="participants-categories"
        role="tablist"
      >
        <button
          v-for="c in categories"
          :key="c.name"
          type="button"
          role="tab"
          class="button-unstyled participants-category"
          :class="{ '-selected': c.name === category.name }"
          :aria-selected="c.name === category.name"
          :aria-label="`${c.label} ${c.count}`"
          :title="c.label"
          @click="selected = c.name"
        >
          <FAIcon
            v-if="c.icon"
            :icon="c.icon"
            fixed-width
          />
          <span
            v-else
            class="participants-emoji"
          >
            <ReactionEmoji
              :name="c.emoji.name"
              :url="c.emoji.url"
            />
          </span>
          <span class="participants-count">{{ c.count }}</span>
        </button>
      </div>

      <div
        ref="list"
        class="participants-list"
        role="tabpanel"
      >
        <UserListRow
          v-for="user in users"
          :key="user.id"
          :user="user"
        />
        <div
          v-if="loading"
          class="participants-state"
        >
          <FAIcon
            icon="circle-notch"
            spin
            size="lg"
            :title="$t('general.loading')"
          />
        </div>
        <div
          v-else-if="failed"
          class="participants-state"
        >
          {{ $t('general.generic_error') }}
          <button
            class="button-unstyled -link participants-retry"
            type="button"
            @click="retry"
          >
            {{ $t('general.retry') }}
          </button>
        </div>
        <div
          v-else-if="users.length === 0"
          class="participants-state"
        >
          {{ $t('search.no_results') }}
        </div>
        <button
          v-else-if="category.hasMore"
          class="button-unstyled -link participants-load-more"
          type="button"
          @click="more"
        >
          {{ $t('status.load_more_participants') }}
        </button>
      </div>

      <template #footer>
        <button
          class="btn button-default participants-close"
          type="button"
          @click="close"
        >
          {{ $t('general.close') }}
        </button>
      </template>
    </DialogModal>
  </teleport>
</template>

<script src="./participants_dialog.js"></script>

<style lang="scss">
@use "../../mixins";

.participants-dialog .dialog-modal.panel {
  width: min(26rem, 90vw);

  .dialog-modal-content {
    padding: 0;
  }

  .participants-categories {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5em;
    padding: 0.75em 1em;
    border-bottom: 1px solid var(--border);

    --emoji-size: 1.25em;
  }

  .participants-category {
    @include mixins.reaction-chip;
    @include mixins.reaction-chip-part;

    // the part's full height would undo the pill's own
    height: var(--_chip-height, 1.9em);
    gap: 0.35em;
    color: var(--text);
    // the pill shows its own state; no square flash on a tap
    -webkit-tap-highlight-color: transparent;

    &:hover,
    &:focus-visible {
      @include mixins.veil;

      outline: none;
    }

    &.-selected {
      @include mixins.reaction-chip-mine;
    }

    @include mixins.touch {
      --_chip-height: 2.4em;
    }
  }

  .participants-emoji {
    display: inline-flex;
    width: var(--emoji-size);
    height: var(--emoji-size);
    align-items: center;
    justify-content: center;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }

  .participants-list {
    height: min(50vh, 30rem);
    overflow-y: auto;
    padding: 0.5em 1em;

    --emoji-size: calc(var(--emojiSize, 32px) / 2);

    .user-list-row {
      padding-block: 0.4em;
    }
  }

  .participants-state,
  .participants-load-more {
    display: block;
    width: 100%;
    padding: 1em 0;
    text-align: center;
  }
}
</style>
